import { useMemo, useState } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "../../components/ui/collapsible";
import ImageDropUpload from "../../components/ImageDropUpload";
import { toast } from "sonner";
import { useLandingConfig } from "../../hooks/useLandingConfig";
import {
    Save, Layout, Plus, Trash2, ArrowUp, ArrowDown, Tag,
    Gem, Sparkles, Star, Sun, Moon, Heart, Crown, Flame, Shield,
    Compass, Coins, Feather, Infinity as InfinityIcon, Gift, Circle,
    Zap, TrendingUp, Eye, EyeOff, ChevronDown, ChevronUp, Search,
} from "lucide-react";

const KEY_REGEX = /^[a-z][a-z0-9_]{0,31}$/;

const ICON_MAP = {
    "": { Icon: Circle, label: "无图标" },
    gem: { Icon: Gem, label: "宝石" },
    sparkles: { Icon: Sparkles, label: "闪光" },
    star: { Icon: Star, label: "星辰" },
    sun: { Icon: Sun, label: "太阳" },
    moon: { Icon: Moon, label: "月" },
    heart: { Icon: Heart, label: "缘分心" },
    crown: { Icon: Crown, label: "皇冠" },
    flame: { Icon: Flame, label: "火焰" },
    shield: { Icon: Shield, label: "护法盾" },
    compass: { Icon: Compass, label: "罗盘" },
    coins: { Icon: Coins, label: "招财币" },
    feather: { Icon: Feather, label: "羽毛" },
    infinity: { Icon: InfinityIcon, label: "无量" },
    gift: { Icon: Gift, label: "礼盒" },
    circle: { Icon: Circle, label: "圆珠" },
    zap: { Icon: Zap, label: "闪电" },
    "trending-up": { Icon: TrendingUp, label: "生意兴隆" },
};

const ICON_KEYS = Object.keys(ICON_MAP);

const EMOJI_OPTIONS = [
    { value: "💰", label: "财运" },
    { value: "🪙", label: "聚财" },
    { value: "📈", label: "生意" },
    { value: "🏪", label: "店铺" },
    { value: "🍀", label: "幸运" },
    { value: "🌟", label: "好运" },
    { value: "🛡️", label: "护法" },
    { value: "🧿", label: "护身" },
    { value: "💕", label: "缘分" },
    { value: "🤝", label: "人缘" },
    { value: "💼", label: "事业" },
    { value: "🏆", label: "成就" },
    { value: "🪄", label: "如意" },
    { value: "🧧", label: "福气" },
    { value: "📚", label: "智慧" },
    { value: "🔮", label: "洞见" },
    { value: "🌳", label: "长寿" },
    { value: "🕊️", label: "安康" },
];

function IconPicker({ value, onChange, testIdPrefix = "" }) {
    const [open, setOpen] = useState(false);
    const current = ICON_MAP[value || ""] || ICON_MAP[""];
    const CurIcon = current.Icon;
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid={`${testIdPrefix}icon-picker-btn`}
                    className="h-9 w-9 shrink-0 rounded-md border border-slate-200 bg-white hover:border-gold-400 hover:bg-gold-50 flex items-center justify-center text-gold-700 transition-colors"
                    title={`选择图标 (当前: ${current.label})`}
                >
                    <CurIcon className="h-4 w-4" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 p-3">
                <div className="text-xs font-semibold text-gold-700 uppercase tracking-wider mb-2">选择图标</div>
                <div className="grid grid-cols-5 gap-1.5">
                    {ICON_KEYS.map((k) => {
                        const { Icon, label } = ICON_MAP[k];
                        const selected = (value || "") === k;
                        return (
                            <button
                                key={k || "none"}
                                type="button"
                                data-testid={`${testIdPrefix}icon-opt-${k || "none"}`}
                                onClick={() => { onChange(k); setOpen(false); }}
                                title={label}
                                className={`h-10 rounded-md border flex items-center justify-center transition-colors ${
                                    selected
                                        ? "border-gold-500 bg-gold-100 text-gold-800 shadow-sm"
                                        : "border-slate-200 bg-white text-slate-600 hover:border-gold-300 hover:bg-gold-50"
                                }`}
                            >
                                <Icon className="h-4 w-4" />
                            </button>
                        );
                    })}
                </div>
                <div className="text-[10px] text-gold-600 mt-2 text-center">
                    落地页读取 <code className="font-mono bg-gold-100 px-1 rounded">icon</code> 字段 · 无对应显示则不渲染
                </div>
            </PopoverContent>
        </Popover>
    );
}

function EmojiPicker({ value, onChange, testIdPrefix = "" }) {
    const [open, setOpen] = useState(false);
    const current = EMOJI_OPTIONS.find((option) => option.value === value);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid={`${testIdPrefix}emoji-picker-button`}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-lg transition-colors hover:border-gold-400 hover:bg-gold-50"
                    title={`选择 Emoji${current ? `（当前：${current.label}）` : ""}`}
                >
                    <span aria-hidden="true">{value || "＋"}</span>
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gold-700">
                    选择 Emoji
                </div>
                <div className="grid grid-cols-6 gap-1.5">
                    <button
                        type="button"
                        data-testid={`${testIdPrefix}emoji-option-none`}
                        onClick={() => {
                            onChange("");
                            setOpen(false);
                        }}
                        className={`flex h-10 items-center justify-center rounded-md border text-xs transition-colors ${
                            !value
                                ? "border-gold-500 bg-gold-100 text-gold-800"
                                : "border-slate-200 bg-white text-slate-500 hover:bg-gold-50"
                        }`}
                        title="不使用 Emoji"
                    >
                        无
                    </button>
                    {EMOJI_OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            data-testid={`${testIdPrefix}emoji-option-${option.label}`}
                            onClick={() => {
                                onChange(option.value);
                                setOpen(false);
                            }}
                            className={`flex h-10 items-center justify-center rounded-md border text-lg transition-colors ${
                                value === option.value
                                    ? "border-gold-500 bg-gold-100 shadow-sm"
                                    : "border-slate-200 bg-white hover:border-gold-300 hover:bg-gold-50"
                            }`}
                            title={option.label}
                        >
                            <span aria-label={option.label}>{option.value}</span>
                        </button>
                    ))}
                </div>
                <div className="mt-2 text-center text-[10px] text-gold-600">
                    财运、生意、幸运、护法、缘分、事业、如意、智慧、长寿
                </div>
            </PopoverContent>
        </Popover>
    );
}

const GRID_OPTIONS = [
    { value: 2, label: "每行 2 件" },
    { value: 3, label: "每行 3 件" },
    { value: 4, label: "每行 4 件" },
    { value: 5, label: "每行 5 件" },
];

const MAX_OPTIONS = [
    { value: 0, label: "全部显示" },
    { value: 3, label: "仅 3 个" },
    { value: 4, label: "仅 4 个" },
    { value: 6, label: "仅 6 个" },
    { value: 8, label: "仅 8 个" },
    { value: 12, label: "仅 12 个" },
];

export default function HomeConfigPage() {
    const { config, setConfig, loading, saving, save } = useLandingConfig();
    const [newKey, setNewKey] = useState("");
    const [newLabel, setNewLabel] = useState("");
    const [newIcon, setNewIcon] = useState("");
    const [newEmoji, setNewEmoji] = useState("");
    const [categoriesOpen, setCategoriesOpen] = useState(false);

    const updateCat = (idx, patch) => setConfig((c) => ({
        ...c,
        categories: c.categories.map((cat, i) => i === idx ? { ...cat, ...patch } : cat),
    }));
    const moveCategory = (from, to) => {
        if (to < 0 || to >= config.categories.length) return;
        const next = [...config.categories];
        const [it] = next.splice(from, 1);
        next.splice(to, 0, it);
        setConfig({ ...config, categories: next });
    };
    const deleteCategory = (idx) => {
        const cat = config.categories[idx];
        if (!window.confirm(`确定删除「${cat.label}」分类？\n\n归入该分类的商品会自动显示为「其他」，商品不会被删除。`)) return;
        setConfig((c) => ({ ...c, categories: c.categories.filter((_, i) => i !== idx) }));
    };
    const addCategory = () => {
        const k = newKey.trim().toLowerCase();
        const l = newLabel.trim();
        if (!k) return toast.error("请填写 key");
        if (!KEY_REGEX.test(k)) return toast.error("key 必须小写英文开头，只含 a-z / 0-9 / _");
        if (k === "other") return toast.error("'other' 是保留字，请换一个 key");
        if (config.categories.some((c) => c.key === k)) return toast.error("这个 key 已存在");
        if (!l) return toast.error("请填写显示名");
        setConfig((c) => ({
            ...c,
            categories: [
                ...c.categories,
                { key: k, label: l, enabled: true, icon: newIcon, emoji: newEmoji },
            ],
        }));
        setNewKey("");
        setNewLabel("");
        setNewIcon("");
        setNewEmoji("");
        toast.success(`已添加「${l}」，别忘了点保存`);
    };

    const doSave = () => save((c) => {
        if (!c.section_title.trim()) { toast.error("栏目标题不能为空"); return false; }
        return true;
    });

    const enabledCount = useMemo(() => config.categories.filter((c) => c.enabled).length, [config]);

    if (loading) return <div className="text-gold-500">加载中...</div>;

    return (
        <div className="space-y-6" data-testid="home-config-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">落地页</div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1 flex items-center gap-3">
                        <Layout className="h-7 w-7 text-gold-600" /> 首页配置
                    </h1>
                    <p className="text-sm text-gold-500 mt-1">
                        主视觉、商品展示、分类、跑马灯、品牌故事 —— 落地页所有可见内容
                    </p>
                </div>
                <Button
                    data-testid="home-save-btn-top"
                    onClick={doSave}
                    disabled={saving}
                    className="h-10 px-5 bg-gold-600 hover:bg-gold-700 text-white gap-2 shrink-0"
                >
                    <Save className="h-4 w-4" /> {saving ? "保存中..." : "保存"}
                </Button>
            </div>

            {/* Section titles */}
            <Card className="p-6 border-slate-200 shadow-sm space-y-4">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600 flex items-center gap-2">
                    <Layout className="h-3.5 w-3.5" /> 落地页文案
                </h2>
                <div className="space-y-1.5">
                    <Label>栏目标题 *</Label>
                    <Input data-testid="lc-title-input" value={config.section_title} onChange={(e) => setConfig({ ...config, section_title: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                    <Label>栏目副标题</Label>
                    <Textarea rows={2} data-testid="lc-subtitle-input" value={config.section_subtitle} onChange={(e) => setConfig({ ...config, section_subtitle: e.target.value })} />
                </div>
            </Card>

            {/* Display method */}
            <Card className="p-6 border-slate-200 shadow-sm space-y-4">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">展示方式</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <Label>每行显示多少件</Label>
                        <Select value={String(config.grid_cols)} onValueChange={(v) => setConfig({ ...config, grid_cols: Number(v) })}>
                            <SelectTrigger data-testid="lc-grid-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {GRID_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label>总共显示多少件</Label>
                        <Select value={String(config.max_items)} onValueChange={(v) => setConfig({ ...config, max_items: Number(v) })}>
                            <SelectTrigger data-testid="lc-max-items-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {MAX_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-gold-50/40">
                    <div>
                        <Label className="cursor-pointer">只显示推荐商品</Label>
                        <div className="text-xs text-slate-500 mt-0.5">开启后落地页只显示后台勾选了「推荐」的商品</div>
                    </div>
                    <Switch data-testid="lc-only-featured" checked={config.only_featured} onCheckedChange={(v) => setConfig({ ...config, only_featured: v })} />
                </div>

                <div className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-gold-50/40">
                    <div>
                        <Label className="cursor-pointer flex items-center gap-1.5">
                            {config.show_all_tab ? <Eye className="h-3.5 w-3.5 text-gold-600" /> : <EyeOff className="h-3.5 w-3.5 text-slate-400" />}
                            显示「所有天珠」总览 Tab
                        </Label>
                        <div className="text-xs text-slate-500 mt-0.5">
                            关闭后落地页顶部的「所有天珠」/「All」标签会被隐藏（需落地页读取此配置）
                        </div>
                    </div>
                    <Switch data-testid="lc-show-all-tab" checked={config.show_all_tab} onCheckedChange={(v) => setConfig({ ...config, show_all_tab: v })} />
                </div>

                <div className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-gold-50/40">
                    <div>
                        <Label className="cursor-pointer flex items-center gap-1.5">
                            <Search className="h-3.5 w-3.5 text-gold-600" />
                            显示商品搜索框
                        </Label>
                        <div className="text-xs text-slate-500 mt-0.5">
                            关闭时落地页天珠专区不显示搜索框（默认关闭）
                        </div>
                    </div>
                    <Switch data-testid="lc-show-search" checked={!!config.show_search} onCheckedChange={(v) => setConfig({ ...config, show_search: v })} />
                </div>
            </Card>

            {/* Dynamic categories */}
            <Card className="p-6 border-slate-200 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600 flex items-center gap-2">
                        <Tag className="h-3.5 w-3.5" /> 眼数分类（自由管理）
                    </h2>
                    <Badge className="bg-gold-100 text-gold-800 border-0">{enabledCount} / {config.categories.length} 启用</Badge>
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="lc-categories-summary">
                    {config.categories.filter((category) => category.enabled).slice(0, 8).map((category) => (
                        <Badge key={category.key} className="border-0 bg-gold-50 text-gold-800">
                            {category.label}
                        </Badge>
                    ))}
                    {enabledCount > 8 && <Badge className="border-0 bg-slate-100 text-slate-600">+{enabledCount - 8}</Badge>}
                    {enabledCount === 0 && <span className="text-xs text-slate-500">还没有启用的眼数分类</span>}
                </div>

                <Collapsible open={categoriesOpen} onOpenChange={setCategoriesOpen}>
                    <CollapsibleTrigger asChild>
                        <Button type="button" variant="outline" data-testid="lc-categories-collapse-button" className="w-full justify-between border-gold-300 text-gold-700 hover:bg-gold-50">
                            <span>{categoriesOpen ? "收起眼数分类管理" : "展开管理全部眼数分类"}</span>
                            {categoriesOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-4 pt-4">
                <p className="text-xs text-slate-500">
                    眼数请使用 eye_N（如 eye_10）· 也可新增 star 等自定义分类 · 停用的不显示在落地页
                </p>
                <div className="space-y-2">
                    {config.categories.length === 0 && (
                        <div className="text-xs text-slate-500 text-center py-6 border border-dashed border-gold-300 rounded-md bg-gold-50/40">
                            还没有分类。下面添加一个吧 ↓
                        </div>
                    )}
                    {config.categories.map((cat, idx) => (
                        <div key={cat.key} data-testid={`lc-cat-row-${cat.key}`}
                            className={`flex items-center gap-2 p-3 rounded-md border transition-all ${cat.enabled ? "border-gold-200 bg-gold-50/40" : "border-slate-200 bg-white opacity-70"}`}>
                            <div className="flex flex-col">
                                <button type="button" onClick={() => moveCategory(idx, idx - 1)} disabled={idx === 0}
                                    className="h-5 w-6 flex items-center justify-center text-gold-500 hover:text-gold-800 disabled:opacity-20"
                                    data-testid={`lc-cat-up-${cat.key}`}>
                                    <ArrowUp className="h-3 w-3" />
                                </button>
                                <button type="button" onClick={() => moveCategory(idx, idx + 1)} disabled={idx === config.categories.length - 1}
                                    className="h-5 w-6 flex items-center justify-center text-gold-500 hover:text-gold-800 disabled:opacity-20"
                                    data-testid={`lc-cat-down-${cat.key}`}>
                                    <ArrowDown className="h-3 w-3" />
                                </button>
                            </div>
                            <EmojiPicker
                                value={cat.emoji || ""}
                                onChange={(v) => updateCat(idx, { emoji: v })}
                                testIdPrefix={`lc-cat-${cat.key}-`}
                            />
                            <IconPicker value={cat.icon || ""} onChange={(v) => updateCat(idx, { icon: v })} testIdPrefix={`lc-cat-${cat.key}-`} />
                            <code className="text-xs font-mono font-bold text-gold-700 bg-white border border-gold-200 rounded px-2 py-1 w-24 text-center shrink-0" title="分类 key (只读)">{cat.key}</code>
                            <Input value={cat.label} onChange={(e) => updateCat(idx, { label: e.target.value })} className="flex-1" placeholder="显示名"
                                data-testid={`lc-cat-label-${cat.key}`} />
                            <Switch data-testid={`lc-cat-toggle-${cat.key}`} checked={cat.enabled} onCheckedChange={(v) => updateCat(idx, { enabled: v })} />
                            <Button type="button" variant="ghost" size="sm" onClick={() => deleteCategory(idx)} className="text-red-600 hover:bg-red-50"
                                data-testid={`lc-cat-delete-${cat.key}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    ))}
                </div>

                <div className="pt-3 border-t border-slate-200">
                    <Label className="text-xs uppercase tracking-wider text-gold-600 font-semibold">新增分类</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                        <EmojiPicker
                            value={newEmoji}
                            onChange={setNewEmoji}
                            testIdPrefix="lc-new-cat-"
                        />
                        <IconPicker value={newIcon} onChange={setNewIcon} testIdPrefix="lc-new-cat-" />
                        <Input placeholder="key (如 eye_10 / star)" value={newKey}
                            onChange={(e) => setNewKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                            className="w-40 font-mono" data-testid="lc-new-cat-key" maxLength={32} />
                        <Input placeholder="显示名 (如 十眼天珠)" value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                            className="flex-1 min-w-[160px]" data-testid="lc-new-cat-label" />
                        <Button type="button" onClick={addCategory} className="gap-1.5 bg-gold-600 hover:bg-gold-700 text-white" data-testid="lc-add-cat-btn">
                            <Plus className="h-4 w-4" /> 添加
                        </Button>
                    </div>
                </div>
                    </CollapsibleContent>
                </Collapsible>
            </Card>

            {/* --- Hero --- */}
            <Card className="p-6 border-slate-200 shadow-sm space-y-4" data-testid="lc-hero-card">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">主视觉 Hero</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <Label>眉题（英文小字）</Label>
                        <Input data-testid="lc-hero-eyebrow" value={config.hero_eyebrow} onChange={(e) => setConfig({ ...config, hero_eyebrow: e.target.value })} placeholder="EST. 1989 — MALAYSIA" />
                    </div>
                    <div className="hidden md:block" />
                    <div className="space-y-1.5">
                        <Label>主标题第一行</Label>
                        <Input data-testid="lc-hero-title-1" value={config.hero_title_1} onChange={(e) => setConfig({ ...config, hero_title_1: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                        <Label>主标题第二行</Label>
                        <Input data-testid="lc-hero-title-2" value={config.hero_title_2} onChange={(e) => setConfig({ ...config, hero_title_2: e.target.value })} />
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label>副标题（多行）</Label>
                    <Textarea rows={3} data-testid="lc-hero-subtitle" value={config.hero_subtitle} onChange={(e) => setConfig({ ...config, hero_subtitle: e.target.value })} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-slate-200">
                    <div className="space-y-1.5">
                        <Label>首屏按钮 · 左（跳选购区）</Label>
                        <Input
                            data-testid="lc-hero-cta-primary"
                            value={config.hero_cta_primary}
                            onChange={(e) => setConfig({ ...config, hero_cta_primary: e.target.value })}
                            placeholder="选购天珠"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>首屏按钮 · 右（跳品牌故事）</Label>
                        <Input
                            data-testid="lc-hero-cta-secondary"
                            value={config.hero_cta_secondary}
                            onChange={(e) => setConfig({ ...config, hero_cta_secondary: e.target.value })}
                            placeholder="品牌故事"
                        />
                    </div>
                </div>

                <div className="pt-3 border-t border-slate-200 space-y-4">
                    <div className="space-y-1.5">
                        <Label>主图（首屏全屏背景图 · 建议 1920×1080 以上横图 · 自动压缩至 2400px 后上传）</Label>
                        <ImageDropUpload
                            value={config.hero_image}
                            onChange={(v) => setConfig({ ...config, hero_image: v || "" })}
                            maxEdge={2400}
                            quality={0.85}
                            uploadToServer
                            previewClassName="aspect-[16/7] max-w-2xl"
                            testIdPrefix="hero-image"
                            helperText="JPG / PNG · 自动压缩至 2400px · 上传后生成图片链接"
                        />
                        <p className="text-xs text-slate-500">
                            上传后 <code className="font-mono">hero_image</code> 保存的是图片链接（非 Base64），落地页读 <code className="font-mono">/api/public/landing-config</code> 即可。
                            建议主体偏中间偏右，左下角留白（落地页会在底部压文字与渐变遮罩）。
                        </p>
                    </div>
                    <div className="space-y-1.5">
                        <Label>主图说明文字（图上小字，如「No.01 — Radiance Collection」）</Label>
                        <Input
                            data-testid="lc-hero-caption"
                            value={config.hero_caption}
                            onChange={(e) => setConfig({ ...config, hero_caption: e.target.value })}
                            placeholder="No.01 — Radiance Collection"
                        />
                    </div>
                </div>
            </Card>

            {/* --- Marquee --- */}
            <Card className="p-6 border-slate-200 shadow-sm space-y-4" data-testid="lc-marquee-card">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">跑马灯</h2>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">{config.show_marquee ? "开启" : "隐藏"}</span>
                        <Switch data-testid="lc-show-marquee" checked={config.show_marquee} onCheckedChange={(v) => setConfig({ ...config, show_marquee: v })} />
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label>文字列表（每行一条）</Label>
                    <Textarea
                        rows={5}
                        data-testid="lc-marquee-items"
                        value={(config.marquee_items || []).join("\n")}
                        onChange={(e) => setConfig({ ...config, marquee_items: e.target.value.split("\n") })}
                        placeholder="福缘珠宝&#10;天珠结缘&#10;传承经典"
                        className="font-mono text-sm"
                    />
                    <div className="text-xs text-gold-500">当前 <span className="font-mono font-bold">{(config.marquee_items || []).filter((x) => x && x.trim()).length}</span> 条</div>
                </div>
            </Card>

            {/* --- Manifesto --- */}
            <Card className="p-6 border-slate-200 shadow-sm space-y-4" data-testid="lc-manifesto-card">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">品牌故事</h2>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">{config.show_manifesto ? "开启" : "隐藏"}</span>
                        <Switch data-testid="lc-show-manifesto" checked={config.show_manifesto} onCheckedChange={(v) => setConfig({ ...config, show_manifesto: v })} />
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label>标题</Label>
                    <Input data-testid="lc-manifesto-title" value={config.manifesto_title} onChange={(e) => setConfig({ ...config, manifesto_title: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                    <Label>正文（多行 · 板块简介）</Label>
                    <Textarea rows={5} data-testid="lc-manifesto-text" value={config.manifesto_text} onChange={(e) => setConfig({ ...config, manifesto_text: e.target.value })} />
                </div>

                {/* Chapters editor */}
                <div className="pt-3 border-t border-slate-200 space-y-3">
                    <div className="flex items-center justify-between">
                        <Label>章节列表（落地页会按顺序渲染）</Label>
                        <Badge className="bg-gold-100 text-gold-800 border-0">{(config.manifesto_chapters || []).length} 章</Badge>
                    </div>
                    {(config.manifesto_chapters || []).length === 0 && (
                        <div className="text-xs text-slate-500 text-center py-4 border border-dashed border-gold-300 rounded-md bg-gold-50/40">
                            还没有章节。下方添加。
                        </div>
                    )}
                    {(config.manifesto_chapters || []).map((ch, idx) => (
                        <div key={idx} data-testid={`lc-chapter-row-${idx}`} className="p-3 rounded-md border border-gold-200 bg-gold-50/40 space-y-2">
                            <div className="flex items-center gap-2">
                                <Input
                                    className="w-16 font-mono text-center font-bold"
                                    value={ch.no || ""}
                                    onChange={(e) => setConfig((c) => ({ ...c, manifesto_chapters: c.manifesto_chapters.map((x, i) => i === idx ? { ...x, no: e.target.value } : x) }))}
                                    placeholder="01"
                                    data-testid={`lc-chapter-no-${idx}`}
                                />
                                <Input
                                    className="flex-1"
                                    value={ch.title || ""}
                                    onChange={(e) => setConfig((c) => ({ ...c, manifesto_chapters: c.manifesto_chapters.map((x, i) => i === idx ? { ...x, title: e.target.value } : x) }))}
                                    placeholder="章节标题（如 严选材质）"
                                    data-testid={`lc-chapter-title-${idx}`}
                                />
                                <div className="flex flex-col">
                                    <button
                                        type="button"
                                        onClick={() => setConfig((c) => {
                                            if (idx === 0) return c;
                                            const next = [...c.manifesto_chapters];
                                            [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                                            return { ...c, manifesto_chapters: next };
                                        })}
                                        disabled={idx === 0}
                                        className="h-5 w-6 flex items-center justify-center text-gold-500 hover:text-gold-800 disabled:opacity-20"
                                        data-testid={`lc-chapter-up-${idx}`}
                                    ><ArrowUp className="h-3 w-3" /></button>
                                    <button
                                        type="button"
                                        onClick={() => setConfig((c) => {
                                            const list = c.manifesto_chapters;
                                            if (idx === list.length - 1) return c;
                                            const next = [...list];
                                            [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
                                            return { ...c, manifesto_chapters: next };
                                        })}
                                        disabled={idx === (config.manifesto_chapters || []).length - 1}
                                        className="h-5 w-6 flex items-center justify-center text-gold-500 hover:text-gold-800 disabled:opacity-20"
                                        data-testid={`lc-chapter-down-${idx}`}
                                    ><ArrowDown className="h-3 w-3" /></button>
                                </div>
                                <Button
                                    type="button" variant="ghost" size="sm"
                                    onClick={() => {
                                        if (!window.confirm(`确定删除第 ${ch.no || idx + 1} 章「${ch.title || "未命名"}」？`)) return;
                                        setConfig((c) => ({ ...c, manifesto_chapters: c.manifesto_chapters.filter((_, i) => i !== idx) }));
                                    }}
                                    className="text-red-600 hover:bg-red-50"
                                    data-testid={`lc-chapter-delete-${idx}`}
                                ><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                            <Textarea
                                rows={3}
                                value={ch.text || ""}
                                onChange={(e) => setConfig((c) => ({ ...c, manifesto_chapters: c.manifesto_chapters.map((x, i) => i === idx ? { ...x, text: e.target.value } : x) }))}
                                placeholder="章节正文"
                                data-testid={`lc-chapter-text-${idx}`}
                            />
                        </div>
                    ))}
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => setConfig((c) => ({
                            ...c,
                            manifesto_chapters: [...(c.manifesto_chapters || []), {
                                no: String((c.manifesto_chapters || []).length + 1).padStart(2, "0"),
                                title: "", text: "",
                            }],
                        }))}
                        className="gap-1.5 border-gold-300 text-gold-700 hover:bg-gold-50"
                        data-testid="lc-chapter-add-btn"
                    >
                        <Plus className="h-4 w-4" /> 新增章节
                    </Button>
                </div>
            </Card>

            {/* --- Product detail modal copy --- */}
            <Card className="p-6 border-slate-200 shadow-sm space-y-4" data-testid="lc-product-copy-card">
                <div>
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">商品详情文案</h2>
                    <p className="text-xs text-slate-500 mt-1">落地页商品弹窗里的备用文字，留空会用默认值。</p>
                </div>
                <div className="space-y-1.5">
                    <Label>无描述时的备用文案</Label>
                    <Input
                        data-testid="lc-product-fallback-desc"
                        value={config.product_fallback_desc}
                        onChange={(e) => setConfig({ ...config, product_fallback_desc: e.target.value })}
                        placeholder="现货臻品，详情请洽店员。"
                    />
                    <div className="text-xs text-slate-500">商品未填「描述」时，弹窗显示这段话。</div>
                </div>
                <div className="space-y-1.5">
                    <Label>库存行模板</Label>
                    <Input
                        data-testid="lc-product-stock-template"
                        value={config.product_stock_template}
                        onChange={(e) => setConfig({ ...config, product_stock_template: e.target.value })}
                        placeholder="现货 {stock} 件 · 与门店库存实时同步"
                        className="font-mono text-sm"
                    />
                    <div className="text-xs text-gold-600">
                        必须保留 <code className="font-mono bg-gold-100 px-1 rounded">{"{stock}"}</code> 占位符，它会被实际库存数替换。
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label>售罄时显示</Label>
                    <Input
                        data-testid="lc-product-soldout-text"
                        value={config.product_soldout_text}
                        onChange={(e) => setConfig({ ...config, product_soldout_text: e.target.value })}
                        placeholder="暂时售罄"
                    />
                </div>
            </Card>

            <Card className="p-4 border-dashed border-gold-300 bg-gold-50/40">
                <div className="text-xs font-semibold uppercase tracking-wider text-gold-700 mb-2">开发者接口 · 落地页可用图标 key</div>
                <div className="text-xs font-mono text-slate-700 space-y-1">
                    <div>公开读取：<code className="bg-white/80 px-1.5 py-0.5 rounded border border-gold-200">GET /api/public/landing-config</code></div>
                    <div>
                        Emoji 字段：<code className="bg-white/80 px-1.5 py-0.5 rounded border border-gold-200">category.emoji</code>
                        ，未读取时可继续使用 <code className="bg-white/80 px-1.5 py-0.5 rounded border border-gold-200">category.icon</code>
                    </div>
                </div>
            </Card>

            {/* Floating save button */}
            <div className="fixed bottom-6 right-6 z-40">
                <Button data-testid="home-save-btn" onClick={doSave} disabled={saving}
                    className="h-12 px-6 bg-gold-600 hover:bg-gold-700 text-white gap-2 shadow-xl shadow-gold-900/20 rounded-full">
                    <Save className="h-4 w-4" /> {saving ? "保存中..." : "保存首页配置"}
                </Button>
            </div>
        </div>
    );
}
